import { dedent } from 'ts-dedent'
import {
  DataWarehouseConnection,
  EXTENSIONS_TABLE_NAME,
  proceduresDataset,
  STORAGE_ERROR_ACCESS_CREATE,
  STORAGE_ERROR_CREATE_TABLES
} from '..'
import { Workflow } from '../..'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'
import { ReadTable } from '../../../components/readtable'
import {
  ColumnDataType,
  LocationType,
  NumericType,
  Provider,
  StoredProcedureScope,
  TableOptimizationInfo,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../../types'
import { formatVariableValue, OUTPUT_TABLE_FQN_VAR_NAME, OUTPUT_TABLE_NAME_VAR_NAME, sortByName } from '../../../utils'

export class BQConnection extends DataWarehouseConnection {
  EXPIRATION_TIME_DAY = 30

  constructor(name: string, tempLocation: string, workspaceLocation: string, extensionsLocation: string) {
    super(name, tempLocation, workspaceLocation, extensionsLocation, Provider.BigQuery)
  }

  castValue(value: string, type: string) {
    return `CAST(${value} AS ${type})`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                  (
                    ${select}
                  ) WHERE 1=0`
    } else {
      return select
    }
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    select = dedent(select)
    const dryRun = codeType === WorkflowCodeType.DryRun
    const cluster =
      !dryRun && optimizeBy?.column
        ? '\n' +
          dedent`
            CLUSTER BY ${optimizeBy.column.name}`
        : ''
    const cache = codeType === WorkflowCodeType.FullNoCache ? '' : ' IF NOT EXISTS'
    return dedent`CREATE TABLE${cache} ${this.quoteName(tablename)}${cluster}
              AS
                ${this.getTempSelectQuery(select, dryRun)};`
  }

  getCloneTableQuery(tablename: string, sourceTable: string, codeType: WorkflowCodeType): string {
    const cache = codeType === WorkflowCodeType.FullNoCache ? '' : ' IF NOT EXISTS'
    return dedent`
      CREATE TABLE${cache} ${this.quoteName(tablename)}
      CLONE ${sourceTable};`
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    select = dedent(select)
    const dryRun = codeType === WorkflowCodeType.DryRun
    let create = ''
    if (codeType === WorkflowCodeType.StoredProcedure) {
      create = 'CREATE TEMPORARY TABLE'
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      // we cannot use only CREATE OR REPLACE here, since it might fail
      // if the clustering column has changed. However, we still use OR REPLACE,
      // even although we just dropped the table, since there might be race conditions
      // when this is done as part of a API workflow that has been called multiple times.
      create = dedent`DROP TABLE IF EXISTS ${this.quoteName(tablename)};
                        CREATE OR REPLACE TABLE`
    } else {
      create = 'CREATE TABLE IF NOT EXISTS'
    }
    const cluster = !dryRun && optimizeBy?.column ? `\nCLUSTER BY ${optimizeBy.column.name}` : ''
    const options =
      codeType === WorkflowCodeType.StoredProcedure
        ? ''
        : dedent`OPTIONS (
                    expiration_timestamp = TIMESTAMP_ADD(
                      CURRENT_TIMESTAMP(), INTERVAL ${this.EXPIRATION_TIME_DAY} DAY
                    )
                  )`
    return dedent`${create} ${this.quoteName(tablename)}${cluster}
                    ${options}
                    AS
                      ${this.getTempSelectQuery(select, codeType === WorkflowCodeType.DryRun)};`
  }

  getCreateTempViewQuery(viewname: string, select: string, codeType: WorkflowCodeType): string {
    return dedent`CREATE OR REPLACE VIEW ${this.quoteName(viewname)}
      OPTIONS (
        expiration_timestamp = TIMESTAMP_ADD(
          CURRENT_TIMESTAMP(), INTERVAL ${this.EXPIRATION_TIME_DAY} DAY
        )
      )
      AS
        ${this.getTempSelectQuery(dedent(select), codeType === WorkflowCodeType.DryRun)};`
  }

  wrapComponentCodeInFlowControlBlock(query: string, codeType: WorkflowCodeType): string {
    if (codeType === WorkflowCodeType.DryRun) {
      return dedent`BEGIN
                  ${query}
                EXCEPTION WHEN ERROR THEN
                  -- do nothing
                END;`
    } else {
      return dedent`BEGIN
                ${query}
                END;`
    }
  }

  getStoredProcedureOutputTableNameExpression(workflow: Workflow, full: boolean, scope: StoredProcedureScope): string {
    const publicVars = workflow.variables.filter((v) => v.scopes?.includes(scope)).sort(sortByName)
    let root = workflow.getStoredProcedureOutputTableFullName(this, scope)
    if (!full) {
      root = root.split('.').pop() as string
    }
    if (publicVars.length === 0) {
      return `'${root}_' || SUBSTRING(TO_HEX(MD5('')), 1, 16)`
    } else {
      return `'${root}_' || SUBSTRING(TO_HEX(MD5(ARRAY_TO_STRING([${publicVars
        .map((v) => `CAST(${this.quoteVariableName(v.name)} AS STRING)`)
        .join(',')}], ','))), 1, 16)`
    }
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string {
    variablePrefix = variablePrefix || ''
    if ([WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(codeType)) {
      return query
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      return dedent`
        ${outputTables.map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)};`).join('\n')}
        BEGIN
          ${query}
        END;
      `
    } else {
      const outputTablesLiteralNames = outputTables.map((x) => `'\\'${x.split('.').pop()}\\''`)
      const outputTablesString = [
        ...outputTablesLiteralNames,
        ...(outputTablesExpressions?.map((v) => `'\\'' || ${v} || '\\''`) || [])
      ].join(', ')
      return dedent`
            BEGIN
              DECLARE ${variablePrefix}tablesCount INT64;
              DECLARE ${variablePrefix}tableNames ARRAY<STRING> DEFAULT [${outputTablesString}];
              EXECUTE IMMEDIATE '''
                SELECT COUNT(1) AS cnt
                FROM \`${this.tempLocation}.__TABLES_SUMMARY__\`
                WHERE table_id IN (''' || ARRAY_TO_STRING(${variablePrefix}tableNames, ', ') || ''')
              ''' INTO ${variablePrefix}tablesCount;
              IF ${variablePrefix}tablesCount < ${outputTables.length + (outputTablesExpressions?.length || 0)} THEN
                BEGIN
                  ${query}
                END;
              END IF;
            END;`
    }
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string> {
    const queries = await workflow.getSubqueries(this, envVars, codeType, schemas)
    const executable = queries.some((q) => q.query)
    if (executable) {
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        let query = await this._getScriptBody(workflow, envVars, queries, codeType, schemas)
        query = dedent`
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                ${preQuery || ''}${query}`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
        return query
      } else {
        return await this._getProcedureBody(workflow, envVars, queries, schemas)
      }
    }
    return ''
  }

  replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ) {
    if (!query || !Array.isArray(variables) || variables.length === 0) {
      return query || ''
    }

    // Sort by name length (descending) to avoid substring replacement issues
    const sortedVariables = [...variables].sort((a, b) => b.name.length - a.name.length)

    if (sortedVariables.length === 0) {
      return query
    }

    if (requiresDynamicSQL) {
      query = dedent(query).replace(/'/g, "\\'")
      query = this.executeDynamicSQL({ sql: dedent(query), addQuotes: true })
    }

    for (const variable of sortedVariables) {
      // Replace the following patterns with the following priority:
      // 1. Pattern for "SELECT * FROM @variable"
      // 2. Pattern for variable inside JSON strings
      // 3. Pattern for generic variable values

      if (requiresDynamicSQL) {
        const replacement = `''' || ${this.quoteVariableName(variable.name)} || '''`
        query = query
          .replace(
            new RegExp(`SELECT\\s+\\*\\s+FROM\\s+\`?@${variable.name}\`?`, 'gi'),
            `SELECT * FROM \`${replacement}\``
          )
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"${replacement}"`)
          .replace(
            new RegExp(`@${variable.name}`, 'g'),
            variable.type === WorkflowComponentParamType.String ? `\\'${replacement}\\'` : replacement
          )
      } else {
        const replacement = this.quoteVariableName(variable.name)
        query = query
          .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"' || ${replacement} || '"`)
          .replace(new RegExp(`@${variable.name}`, 'g'), replacement)
      }
    }

    return query
  }

  paramTypeToDataProviderType(inputType: WorkflowComponentParamType) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'STRING'
      case WorkflowComponentParamType.Number:
        return 'FLOAT64'
      case WorkflowComponentParamType.Boolean:
        return 'BOOLEAN'
      default:
        return 'STRING'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'STRING'
      case ColumnDataType.Number:
        return 'FLOAT64'
      case ColumnDataType.Boolean:
        return 'BOOLEAN'
      case ColumnDataType.Geography:
        return 'GEOGRAPHY'
      case ColumnDataType.Binary:
        return 'BYTES'
      case ColumnDataType.Date:
        return 'DATE'
      case ColumnDataType.Time:
        return 'TIME'
      case ColumnDataType.Datetime:
        return 'DATETIME'
      case ColumnDataType.Timestamp:
        return 'TIMESTAMP'
      default:
        return 'STRING'
    }
  }

  wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean,
    scope: StoredProcedureScope
  ): string {
    const procedureParams = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type)}`)
    const declareVariables = workflow.variables
      .filter((v) => !v.scopes?.includes(scope))
      .map(
        (input) =>
          `DECLARE ${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(
            input.type
          )} DEFAULT ${formatVariableValue(input)};`
      )
    const hasOutput = workflow.hasStoredProcedureOutputTable(scope)
    let outputTableVars = ''
    if (hasOutput || isImmutable) {
      declareVariables.push(`DECLARE ${OUTPUT_TABLE_NAME_VAR_NAME} STRING;`)
      declareVariables.push(`DECLARE ${OUTPUT_TABLE_FQN_VAR_NAME} STRING;`)
      const outputTableNameVariable = `SET ${OUTPUT_TABLE_NAME_VAR_NAME} = ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        false,
        scope
      )};`
      const outputTableFqnVariable = `SET ${OUTPUT_TABLE_FQN_VAR_NAME} = ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        true,
        scope
      )};`
      outputTableVars = dedent`${outputTableNameVariable}
                                 ${outputTableFqnVariable}`
    }
    const defaultValues = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map(
        (input) =>
          `SET ${this.quoteVariableName(input.name)} = COALESCE(${this.quoteVariableName(
            input.name
          )}, ${formatVariableValue(input)});`
      )
    const comment = this._getStoredProcedureEmbeddedComment(workflow)
    const returnTable = hasOutput
      ? workflow.getStoredProcedureOutputIsTableFullContent(this, scope)
        ? dedent`
          EXECUTE IMMEDIATE
          REPLACE (
          '''SELECT * FROM \`##TABLENAME##\`''',
          '##TABLENAME##',
          ${OUTPUT_TABLE_FQN_VAR_NAME}
          );`
        : dedent`
          SELECT ${OUTPUT_TABLE_FQN_VAR_NAME} AS workflowOutputTable;`
      : ''
    return dedent`
                  CREATE OR REPLACE PROCEDURE
                    ${this.quoteName(`${this.workspaceLocation}.${name}`)}(
                      ${procedureParams.join(',\n')}
                  )
                  BEGIN
                    ${includeDiagram ? comment : ''}
                    ${declareVariables.join('\n')}
                    ${defaultValues.join('\n')}
                    ${outputTableVars}
                    ${body}
                    ${returnTable}
                  END;`
  }

  getRemoveStoredProcedureQueries(name: string): Array<string> {
    return [dedent`DROP PROCEDURE IF EXISTS ${this.quoteName(name)}`]
  }

  /**
   * Override to provide direct SQL compilation for MCP tools in BigQuery
   * This avoids the stored procedure overhead by returning the workflow SQL directly
   */
  async getAsDirectSQL(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    scope: StoredProcedureScope,
    schemas?: WorkflowTableSchemas
  ): Promise<string | null> {
    // Only support direct SQL for MCP tools in BigQuery
    if (scope !== StoredProcedureScope.McpTool) {
      return null
    }

    try {
      // Get subqueries and compact into CTEs
      const queries = await workflow.getSubqueries(this, envVars, WorkflowCodeType.StoredProcedure, schemas)
      const compactedQueries = await this._compactQueriesIntoCTEs(workflow, queries, envVars)

      if (compactedQueries.length === 0) {
        return null
      }

      // Only use direct SQL if ALL queries were successfully compacted into CTEs
      // If there are multiple queries that couldn't be compacted, fall back to stored procedure
      if (compactedQueries.length > 1) {
        // Multiple queries means the workflow couldn't be fully compacted into a single CTE
        // This is a complex workflow that needs stored procedure execution
        return null
      }

      // Single compacted query - extract clean CTE
      const singleQuery = compactedQueries[0]
      const component = workflow.components[singleQuery.componentName]

      // Verify this is a CTE-compactable component
      if (!component.canBeConvertedToCTE(workflow, singleQuery.componentName)) {
        return null
      }

      // Find the CTE chain for this query to rebuild it cleanly
      const cteChain = this._findCTEChain(
        workflow,
        queries,
        queries.find((q) => q.componentName === singleQuery.componentName)!
      )

      if (cteChain.length <= 1) {
        // Single non-chained query, can't optimize
        return null
      }

      // Build clean CTE SQL without CREATE TABLE wrapper
      const cleanCTE = await this._buildCompactedCTEQuery(workflow, cteChain, envVars)
      return cleanCTE || null
    } catch {
      // If compilation fails, return null (fall back to stored procedure)
      return null
    }
  }

  extractErrorLineNumberFromApiResponse(response: string): number | null {
    const match = response.match(/\[(\d+):\d+\]/)
    if (match) {
      return parseInt(match[1])
    } else {
      return null
    }
  }

  getSourceTablePermissionsQuery(workflow: Workflow) {
    const tables: Array<string> = []
    for (const [componentName, component] of Object.entries(workflow.components)) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        if (!tables.includes(tablename)) {
          tables.push(tablename)
        }
      }
    }
    if (tables.length === 0) {
      return ''
    }
    const query = dedent`DECLARE tables ARRAY<STRING> DEFAULT [${tables.map((x) => `'${x}'`).join(', ')}];
        DECLARE noAccess ARRAY<STRING> DEFAULT [];
        DECLARE i INT64 DEFAULT 0;

        LOOP
          SET i = i + 1;
          IF i > ARRAY_LENGTH(tables) THEN
            LEAVE;
          END IF;
          BEGIN
            EXECUTE IMMEDIATE
              'SELECT * FROM \`' || tables[ORDINAL(i)] || '\` WHERE 1=0';
          EXCEPTION WHEN ERROR THEN
            SET noAccess = ARRAY_CONCAT(noAccess, [tables[ORDINAL(i)]]);
          END;
        END LOOP;

        SELECT noAccess AS noAccess;`

    return query
  }

  getStorageSetupQueries(options?: { location?: string }) {
    const location = options?.location || 'us'
    return [
      dedent`
        DECLARE errorcodeCartoTemp STRING DEFAULT '';
        DECLARE errorcodeCartoWorkspace STRING DEFAULT '';
        DECLARE errorcodeCartoExtensions STRING DEFAULT '';
        DECLARE resultCartoTemp STRING DEFAULT '';
        DECLARE resultCartoWorkspace STRING DEFAULT '';
        DECLARE resultCartoExtensions STRING DEFAULT '';
        BEGIN
          CREATE TABLE IF NOT EXISTS \`${this.tempLocation}.__testtable\` AS SELECT 1 AS N;
          DROP TABLE IF EXISTS \`${this.tempLocation}.__testtable\`;
        EXCEPTION WHEN ERROR THEN
          IF @@error.message LIKE '%Access Denied%' THEN
            SET errorcodeCartoTemp = '${STORAGE_ERROR_CREATE_TABLES}';
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS \`${this.tempLocation}\`
              OPTIONS (
                location='${location}',
                default_table_expiration_days=172800
              );
            EXCEPTION WHEN ERROR THEN
              SET errorcodeCartoTemp = '${STORAGE_ERROR_ACCESS_CREATE}';
            END;
          END IF;
        END;
        BEGIN
          CREATE TABLE IF NOT EXISTS \`${this.workspaceLocation}.__testtable\` AS SELECT 1 AS N;
          DROP TABLE IF EXISTS \`${this.workspaceLocation}.__testtable\`;
        EXCEPTION WHEN ERROR THEN
          IF @@error.message LIKE '%Access Denied%' THEN
            SET errorcodeCartoWorkspace = '${STORAGE_ERROR_CREATE_TABLES}';
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS \`${this.workspaceLocation}\`
              OPTIONS (
                location='${location}',
                default_table_expiration_days=172800
              );
            EXCEPTION WHEN ERROR THEN
              SET errorcodeCartoWorkspace = '${STORAGE_ERROR_ACCESS_CREATE}';
            END;
          END IF;
        END;
        BEGIN
          CREATE TABLE IF NOT EXISTS \`${this.extensionsLocation}.__testtable\` AS SELECT 1 AS N;
          DROP TABLE IF EXISTS \`${this.extensionsLocation}.__testtable\`;
        EXCEPTION WHEN ERROR THEN
          IF @@error.message LIKE '%Access Denied%' THEN
            SET errorcodeCartoExtensions = '${STORAGE_ERROR_CREATE_TABLES}';
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS \`${this.extensionsLocation}\`
              OPTIONS (
                location='${location}',
                default_table_expiration_days=172800
              );
            EXCEPTION WHEN ERROR THEN
              SET errorcodeCartoExtensions = '${STORAGE_ERROR_ACCESS_CREATE}';
            END;
          END IF;
        END;
        IF (errorcodeCartoTemp = '') THEN
          SET resultCartoTemp = '{"location": "carto_temp", "success": true}';
        ELSE
          SET resultCartoTemp = '{"location": "carto_temp", "success": false, "error": "' || errorcodeCartoTemp ||'"}';
        END IF;
        IF (errorcodeCartoWorkspace = '') THEN
          SET resultCartoWorkspace = '{"location": "carto_workspace", "success": true}';
        ELSE
          SET resultCartoWorkspace = '{"location": "carto_workspace", "success": false, "error": "' || errorcodeCartoWorkspace ||'"}';
        END IF;
        IF (errorcodeCartoExtensions = '') THEN
          SET resultCartoExtensions = '{"location": "carto_extensions", "success": true}';
        ELSE
          SET resultCartoExtensions = '{"location": "carto_extensions", "success": false, "error": "' || errorcodeCartoExtensions ||'"}';
        END IF;
        SELECT CONCAT('[', resultCartoTemp, ', ', resultCartoWorkspace, ', ', resultCartoExtensions, ']') AS result;`
    ]
  }

  getStoredProcedureCacheInvalidationQueries(name: string): Array<string> {
    const tablePrefix = this.getStoredProcedureOutputTableNameForProcedureName(name.split('.').pop() as string)
    return [
      dedent`
      FOR table IN (
        SELECT
          table_name
        FROM
          ${this.quoteName(this.workspaceLocation)}.INFORMATION_SCHEMA.TABLES
        WHERE
          table_name LIKE '${tablePrefix}%')
      DO
        EXECUTE IMMEDIATE FORMAT('DROP TABLE ${this.quoteName(this.workspaceLocation)}.%s', table.table_name);
      END FOR;`
    ]
  }

  getTableListQuery(filter: string, locationType: LocationType = LocationType.Temp): string {
    return dedent`
      SELECT table_name
      FROM ${this.quoteName(this.getLocation(locationType))}.INFORMATION_SCHEMA.TABLES
      WHERE table_name LIKE '${filter}';`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'NUMERIC':
      case 'BIGNUMERIC':
      case 'INT64':
      case 'FLOAT64':
        return ColumnDataType.Number
      case 'BOOL':
        return ColumnDataType.Boolean
      case 'GEOGRAPHY':
        return ColumnDataType.Geography
      case 'DATE':
        return ColumnDataType.Date
      case 'TIME':
        return ColumnDataType.Time
      case 'DATETIME':
        return ColumnDataType.Datetime
      case 'TIMESTAMP':
        return ColumnDataType.Timestamp
      case 'BYTES':
        return ColumnDataType.Binary
      default:
        return ColumnDataType.String
    }
  }

  numericTypeToDataProviderType(numericType: NumericType) {
    return numericType === NumericType.Float ? 'FLOAT64' : 'INT64'
  }

  quoteName(name: string) {
    return `\`${name.replace(/`/g, '')}\``
  }

  quoteMultilineString(str: string) {
    return dedent`'''${str}'''`
  }

  quoteVariableName(name: string) {
    return `\`${name}\``
  }

  getExtensionUninstallQuery(name: string): string {
    return dedent`
      BEGIN
        DECLARE i INT64 DEFAULT 0;
        DECLARE procedures ARRAY<STRING>;

        BEGIN
          SET procedures = (
              SELECT SPLIT(procedures, ',')
              FROM ${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}
              WHERE name = '${name}'
          );
        EXCEPTION WHEN ERROR THEN
          SET procedures = [];
        END;
        LOOP
            SET i = i + 1;
            IF i > ARRAY_LENGTH(procedures) THEN
                LEAVE;
            END IF;
            BEGIN
              EXECUTE IMMEDIATE 'DROP PROCEDURE ${this.extensionsLocation}.' || procedures[ORDINAL(i)];
            EXCEPTION WHEN ERROR THEN
              -- do nothing
            END;
        END LOOP;

        DELETE FROM ${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}
        WHERE name = '${name}';
      END;`
  }

  getStoredProceduresQuery(): string | null {
    const path = `${this.workspaceLocation.split('.')[0]}.${proceduresDataset}`
    return dedent`begin
                select
                  ddl
                from \`${path}.INFORMATION_SCHEMA.ROUTINES\`
                where routine_type = 'PROCEDURE';
              exception when error then
                with dummy as (
                  select 'dummy' as ddl
                )
                select
                  ddl
                from
                  dummy
                where false;
              end;`
  }

  adaptComponent(component: WorkflowComponent) {
    const whiteList = ['native.geometrytogeography']
    if (!whiteList.includes(component.metadata.name)) {
      for (const input of component.inputs) {
        if (input.type === WorkflowComponentParamType.Column && input.dataType) {
          input.dataType = (input.dataType as Array<ColumnDataType>).filter((x) => x !== ColumnDataType.Geometry)
        }
      }
    }
    component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.BigQuery))
  }

  getOutputSchemasQueries(tableNames: Array<string>): string {
    return dedent`
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          data_type AS db_type,
          CASE data_type
            WHEN 'NUMERIC' THEN '${ColumnDataType.Number}'
            WHEN 'BIGNUMERIC' THEN '${ColumnDataType.Number}'
            WHEN 'INT64' THEN '${ColumnDataType.Number}'
            WHEN 'FLOAT64' THEN '${ColumnDataType.Number}'
            WHEN 'BOOL' THEN '${ColumnDataType.Boolean}'
            WHEN 'GEOGRAPHY' THEN '${ColumnDataType.Geography}'
            WHEN 'DATE' THEN '${ColumnDataType.Date}'
            WHEN 'TIME' THEN '${ColumnDataType.Time}'
            WHEN 'DATETIME' THEN '${ColumnDataType.Datetime}'
            WHEN 'TIMESTAMP' THEN '${ColumnDataType.Timestamp}'
            WHEN 'BYTES' THEN '${ColumnDataType.Binary}'
            ELSE '${ColumnDataType.String}'
          END AS type,
          ordinal_position
        FROM \`${this.tempLocation}.INFORMATION_SCHEMA.COLUMNS\`
        WHERE table_name IN (${tableNames.map((t) => `'${t}'`).join(', ')})
      )
      SELECT
        CONCAT('${this.tempLocation}.', ANY_VALUE(table_name)) AS fqn,
        TO_JSON_STRING(
          ARRAY_AGG(
            STRUCT(name, type, db_type)
            ORDER BY ordinal_position ASC
          )
        ) AS schema
      FROM cols
      GROUP BY table_name;`
  }
}
